import type { RemoveRingAction } from "../types/action";
import type { PlayerColor } from "../types/common";
import type { YinshState } from "../types/game-state";

function getOpponent(player: PlayerColor): PlayerColor {
  return player === "WHITE" ? "BLACK" : "WHITE";
}

function resolveMarkerExhaustion(state: YinshState): YinshState {
  const [p0, p1] = state.players;
  if (p0.ringsScored > p1.ringsScored) {
    return { ...state, phase: "GAME_END", winner: p0.color };
  }
  if (p1.ringsScored > p0.ringsScored) {
    return { ...state, phase: "GAME_END", winner: p1.color };
  }
  return { ...state, phase: "GAME_END", winner: null, isDraw: true };
}

export function applyRemoveRing(
  state: YinshState,
  action: RemoveRingAction,
): YinshState {
  const newBoard = { ...state.board };
  newBoard[action.position] = null;

  const updatedPlayers: typeof state.players = [
    state.players[0].color === action.player
      ? {
          ...state.players[0],
          ringsOnBoard: state.players[0].ringsOnBoard - 1,
          ringsScored: state.players[0].ringsScored + 1,
        }
      : state.players[0],
    state.players[1].color === action.player
      ? {
          ...state.players[1],
          ringsOnBoard: state.players[1].ringsOnBoard - 1,
          ringsScored: state.players[1].ringsScored + 1,
        }
      : state.players[1],
  ];

  const scoringPlayer = updatedPlayers.find((p) => p.color === action.player);
  const ringsToWin = 3;

  // Win condition
  if (scoringPlayer && scoringPlayer.ringsScored >= ringsToWin) {
    return {
      ...state,
      board: newBoard,
      players: updatedPlayers,
      phase: "GAME_END",
      winner: action.player,
      lastMover: null,
    };
  }

  // More pending rows for current player
  if (state.pendingRows.length > 0) {
    return { ...state, board: newBoard, players: updatedPlayers };
  }

  // Opponent has rows to resolve
  if (state.opponentPendingRows.length > 0) {
    const opponent = getOpponent(action.player);
    return {
      ...state,
      board: newBoard,
      players: updatedPlayers,
      currentPlayer: opponent,
      pendingRows: state.opponentPendingRows,
      opponentPendingRows: [],
      rowsRemovedThisTurn: 0,
    };
  }

  // All resolution complete — turn passes to the opponent of the ORIGINAL
  // mover (state.lastMover), not the opponent of whoever happened to remove
  // the final ring. This matters when the mover's move created an opponent
  // 5-row: the opponent resolved their row, but the next turn must still go
  // to the opponent (since the mover already took their move).
  // applyMoveRing always sets lastMover before entering REMOVING_ROW; the
  // `?? action.player` fallback is defensive only — for an own-row
  // resolution it coincides with the correct value anyway.
  const originalMover = state.lastMover ?? action.player;
  const nextState: YinshState = {
    ...state,
    board: newBoard,
    players: updatedPlayers,
    phase: "PLAYING",
    currentPlayer: getOpponent(originalMover),
    turn: state.turn + 1,
    pendingRows: [],
    opponentPendingRows: [],
    rowsRemovedThisTurn: 0,
    markerPlacedAt: null,
    lastMover: null,
  };

  if (state.markersPool <= 0) {
    return resolveMarkerExhaustion(nextState);
  }

  return nextState;
}
